import { BufferGeometry, Float32BufferAttribute, Mesh } from 'three';
import { crossingDip, CuttingQuery } from '../track/crossing';
import type { SceneryPlan } from '../track/scenery';
import { TrackProximity } from '../track/scenery';
import type { TrackData } from '../track/types';
import { look, seaPlan, type SeaPlan } from './look';
import { vertexColorToon } from './toon';

/**
 * Ground: the circuit's own height next to the track (its runoff strip is part
 * of the track mesh), rolling golden hills beyond, a proper hill under the
 * hilltop town. Faceted (flat normals) for the low-poly look.
 */
export class Terrain {
  readonly prox: TrackProximity;
  private phase: number[];
  /** The ground's own level along the circuit: the road's height, except where it dips into a cutting. */
  private natural: number[];
  readonly cut: CuttingQuery | null;
  /** The cutting's footprint (sim coords). */
  private cutBox: { minX: number; maxX: number; minY: number; maxY: number } | null = null;
  /** Where the terrain is drawn finer: a crossover's cutting, a banked curve, sharp crests and dips. */
  private fine: Box[] = [];
  /** The day's sea, if its setting has one: past the coast the land drops under it. */
  private sea: SeaPlan | null = seaPlan();

  constructor(private track: TrackData, private plan: SceneryPlan, seed: number) {
    this.prox = new TrackProximity(track, 30);
    this.phase = Array.from({ length: 8 }, (_, i) => Math.sin(seed * 12.9898 + i * 78.233) * 43758.5453 % (Math.PI * 2));
    const c = track.crossing ?? null;
    this.natural = c ? track.elev.map((h, i) => h + crossingDip(c, track.s[i], track.length)) : track.elev;
    this.cut = c ? new CuttingQuery(track, c) : null;
    if (c) {
      const box = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
      const pad = c.cutHalf + 12;
      for (let i = 0; i < track.n; i++) {
        if (crossingDip(c, track.s[i], track.length) <= 0.02) continue;
        box.minX = Math.min(box.minX, track.px[i] - pad);
        box.maxX = Math.max(box.maxX, track.px[i] + pad);
        box.minY = Math.min(box.minY, track.py[i] - pad);
        box.maxY = Math.max(box.maxY, track.py[i] + pad);
      }
      if (box.minX < box.maxX) {
        this.cutBox = box;
        this.fine.push(box);
      }
    }
    if (track.bank) {
      const box = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
      const pad = track.halfWidth + 34;
      for (let i = 0; i < track.n; i++) {
        if (Math.abs(track.bank[i]) < 0.02) continue;
        box.minX = Math.min(box.minX, track.px[i] - pad);
        box.maxX = Math.max(box.maxX, track.px[i] + pad);
        box.minY = Math.min(box.minY, track.py[i] - pad);
        box.maxY = Math.max(box.maxY, track.py[i] + pad);
      }
      if (box.minX < box.maxX) this.fine.push(box);
    }
    // Jumps, a plunge, a sharp crest: the coarse grid can't follow the road's height there, and its straight
    // lines between grid points would come up through the road.
    this.fine.push(...roughStretches(track));
    // Patches mustn't overlap (each one's edge follows the coarse grid): any that would, become one.
    this.fine = mergeBoxes(this.fine, 24);
  }

  /** Down in a crossover's cutting (walls included): the cutting's floor there, else null. */
  private cutFloor(sx: number, sy: number): number | null {
    const b = this.cutBox;
    if (!b || !this.cut || sx < b.minX || sx > b.maxX || sy < b.minY || sy > b.maxY) return null;
    const p = this.cut.project(sx, sy);
    // Out to a little past the wall's back, so the terrain's slope up to the ground hides under the wall's cap.
    if (!p || p.dip <= 0.02 || p.lat > this.cut.c.cutHalf + 3.2) return null;
    return p.floor;
  }

  /** Ground height at simulation coordinates: what scenery stands on. Near the track it's the track's own height. */
  heightAt(sx: number, sy: number): number {
    return this.ground(sx, sy, false);
  }

  /**
   * The terrain mesh sits a little lower than the ground right along the
   * circuit, tucked under the track's runoff strip: its coarse triangles can
   * then never poke through the road where the road dips.
   */
  private ground(sx: number, sy: number, mesh: boolean): number {
    const floor = this.cutFloor(sx, sy);
    if (floor !== null) return floor - (mesh ? 0.45 : 0);
    const [i, d] = this.prox.nearest(sx, sy, 300);
    let t = 1;
    let base = 0;
    let dip = 0;
    if (i >= 0) {
      const bar = Math.max(this.track.barrierLeft[i], this.track.barrierRight[i]);
      t = smooth((d - bar - 18) / 280);
      base = this.natural[i];
      // A banked road: the ground beside it carries on from the edge it's next to.
      const bank = this.track.bank;
      if (bank && bank[i] !== 0) {
        const lat = (sx - this.track.px[i]) * -this.track.ty[i] + (sy - this.track.py[i]) * this.track.tx[i];
        const edge = this.track.halfWidth + this.track.curbWidth;
        base += (lat < -edge ? -edge : lat > edge ? edge : lat) * bank[i];
      }
      if (mesh) dip = 0.45 * (1 - smooth((d - bar - 2) / 12));
    }
    const p = this.phase;
    const u = sx / 190;
    const v = sy / 190;
    let h = 0.5 + 0.24 * Math.sin(u + p[0]) * Math.cos(v * 0.8 + p[1]) + 0.17 * Math.sin(u * 2.3 + v * 1.1 + p[2]) + 0.1 * Math.sin(v * 2.9 - u * 0.7 + p[3]);
    h = Math.max(0, h) * 46;
    const town = this.plan.town;
    const dx = sx - town.x;
    const dy = sy - town.y;
    const r = Math.sqrt(dx * dx + dy * dy);
    // A broad rounded hill with a flattened top for the town.
    const mound = town.height * smooth(1 - (r - town.radius * 0.8) / 620);
    // At the circuit: its own height. Further out: the hills (and past a coast, the sea floor; never right by the road).
    const shoreFree = i >= 0 ? smooth((d - Math.max(this.track.barrierLeft[i], this.track.barrierRight[i]) - 25) / 60) : 1;
    return base + t * (Math.max(h, mound) - base) - dip - this.shore(sx, sy) * shoreFree;
  }

  /** How far the land drops past the day's coast (0 inland, or with no sea): a beach, then the sea floor. */
  shore(sx: number, sy: number): number {
    const s = this.sea;
    if (!s) return 0;
    const ox = sx - s.cx;
    const oy = sy - s.cy;
    const along = ox * s.dx + oy * s.dy;
    const lat = -ox * s.dy + oy * s.dx;
    return 60 * smooth((along - this.coastAlong(lat)) / 120);
  }

  /**
   * The sea's sheet: a fan over the half-disc past the coast, its straight edge inland under the land but past
   * the circuit's furthest road (the coast is 130 m beyond it; bays come in up to 98 m).
   */
  private seaInto(sea: SeaPlan, c: { r: number; g: number; b: number }, level: number, pos: number[], col: number[], nor: number[]): void {
    const R = 6500;
    const mid = Math.atan2(sea.dy, sea.dx);
    const start = sea.coast - 105;
    const cx = sea.cx + sea.dx * start;
    const cy = sea.cy + sea.dy * start;
    const steps = 64;
    const at = (k: number): [number, number] => {
      const a = mid - Math.PI / 2 + (k / steps) * Math.PI;
      return [cx + Math.cos(a) * R, cy + Math.sin(a) * R];
    };
    for (let k = 0; k < steps; k++) {
      const [ax, ay] = at(k);
      const [bx, by] = at(k + 1);
      // Counter-clockwise seen from above (sim y is three's -z).
      pos.push(cx, level, -cy, ax, level, -ay, bx, level, -by);
      for (let q = 0; q < 3; q++) {
        col.push(c.r, c.g, c.b);
        nor.push(0, 1, 0);
      }
    }
  }

  /** The day's sea (null if its setting has none). */
  get seaPlan(): SeaPlan | null {
    return this.sea;
  }

  /** How far out the coast is at `lat` across the sea's direction (bays and headlands along it), m. */
  coastAlong(lat: number): number {
    const s = this.sea;
    if (!s) return Infinity;
    return s.coast + 70 * Math.sin(lat / 210 + this.phase[6]) + 28 * Math.sin(lat / 71 + this.phase[7]);
  }

  build(cell = 14): Mesh {
    const b = this.plan.bounds;
    const half = Math.max(b.maxX - b.minX, b.maxY - b.minY) / 2 + 1350;
    const n = Math.ceil((half * 2) / cell);
    const x0 = b.cx - half;
    const y0 = b.cy - half;
    const hs = new Float32Array((n + 1) * (n + 1));
    for (let j = 0; j <= n; j++) {
      for (let i = 0; i <= n; i++) hs[j * (n + 1) + i] = this.ground(x0 + i * cell, y0 + j * cell, true);
    }
    const pos: number[] = [];
    const col: number[] = [];
    const nor: number[] = [];
    // Smooth normals from the height grid: soft rolling hills, flat field colours.
    const normalAt = (i: number, j: number): [number, number, number] => {
      const h = (a: number, b: number): number => hs[Math.max(0, Math.min(n, b)) * (n + 1) + Math.max(0, Math.min(n, a))];
      const dx = (h(i + 1, j) - h(i - 1, j)) / (2 * cell);
      const dy = (h(i, j + 1) - h(i, j - 1)) / (2 * cell);
      // Sim (x, y) -> three (x, -z): d/dz = -d/dy.
      const nx = -dx;
      const ny = 1;
      const nz = dy;
      const l = Math.hypot(nx, ny, nz);
      return [nx / l, ny / l, nz / l];
    };
    // A crossover's cutting (sheer walls) and a banked curve (a tilted road) need finer ground than the rest.
    // Those cells get their own patches.
    const patches: [number, number, number, number][] = this.fine.map((cb) => [
      Math.max(0, Math.floor((cb.minX - x0) / cell)),
      Math.min(n, Math.ceil((cb.maxX - x0) / cell)),
      Math.max(0, Math.floor((cb.minY - y0) / cell)),
      Math.min(n, Math.ceil((cb.maxY - y0) / cell)),
    ]);
    // The setting's ground (look.ts): Tuscany's golden fields, or greener, or drier.
    const { grass: base, fieldA, fieldB, fieldC, shares } = look().ground;
    // How much of the land is each kind of field (Tuscany's: 20%, 10%, 12%).
    const [sA, sB, sC] = shares ?? [0.2, 0.1, 0.12];
    const p = this.phase;
    for (let j = 0; j < n; j++) {
      for (let i = 0; i < n; i++) {
        if (patches.some((p) => i >= p[0] && i < p[1] && j >= p[2] && j < p[3])) continue;
        const sxA = x0 + i * cell;
        const syA = y0 + j * cell;
        // Field patches: large rotated blocks in a few tones, only away from the track.
        const fx = (sxA * 0.7 + syA * 0.71) / 150;
        const fy = (-sxA * 0.71 + syA * 0.7) / 110;
        const cellId = Math.floor(fx + p[4]) * 131 + Math.floor(fy + p[5]) * 17;
        const r = Math.abs(Math.sin(cellId * 12.9898) * 43758.5453) % 1;
        const [, dTrack] = this.prox.nearest(sxA, syA, 120);
        const nearTrack = dTrack < 60;
        let c = base;
        if (!nearTrack) {
          if (r < sA) c = fieldA;
          else if (r < sA + sB) c = fieldB;
          else if (r < sA + sB + sC) c = fieldC;
        }
        const quad = [
          [i, j],
          [i + 1, j],
          [i + 1, j + 1],
          [i, j + 1],
        ];
        const P = quad.map(([a, bb]) => {
          const sx = x0 + a * cell;
          const sy = y0 + bb * cell;
          return [sx, hs[bb * (n + 1) + a], -sy];
        });
        // Two triangles, counter-clockwise seen from above (normals up).
        for (const tri of [
          [0, 1, 2],
          [0, 2, 3],
        ]) {
          for (const k of tri) {
            pos.push(P[k][0], P[k][1], P[k][2]);
            col.push(c.r, c.g, c.b);
            const nn = normalAt(quad[k][0], quad[k][1]);
            nor.push(nn[0], nn[1], nn[2]);
          }
        }
      }
    }
    for (const p of patches) this.buildPatch(p, x0, y0, cell, n, hs, pos, col, nor);
    // The day's sea, if it has one: a flat sheet from under the coast out to the horizon, in the ground's own
    // mesh (no extra draw).
    const L = look();
    if (this.sea && L.sea) this.seaInto(this.sea, L.sea.color, this.sea.level, pos, col, nor);
    const g = new BufferGeometry();
    g.setAttribute('position', new Float32BufferAttribute(pos, 3));
    g.setAttribute('color', new Float32BufferAttribute(col, 3));
    g.setAttribute('normal', new Float32BufferAttribute(nor, 3));
    const m = new Mesh(g, vertexColorToon());
    m.receiveShadow = true;
    return m;
  }

  /**
   * The ground over a crossover's cutting, a banked curve or sharp crests, on a grid about 1.6 m fine.
   * Its edge follows the coarse grid's own (interpolated) heights so there are no cracks.
   */
  private buildPatch(
    [i0, i1, j0, j1]: [number, number, number, number],
    x0: number,
    y0: number,
    cell: number,
    n: number,
    hs: Float32Array,
    pos: number[],
    col: number[],
    nor: number[],
  ): void {
    const f = Math.max(2, Math.ceil(cell / 1.6));
    const fc = cell / f;
    const W = (i1 - i0) * f;
    const H = (j1 - j0) * f;
    const coarse = (i: number, j: number): number => hs[j * (n + 1) + i];
    const h = new Float32Array((W + 1) * (H + 1));
    for (let J = 0; J <= H; J++) {
      for (let I = 0; I <= W; I++) {
        const onX = I === 0 || I === W;
        const onY = J === 0 || J === H;
        let v: number;
        if (onX || onY) {
          // Along the patch's edge: the coarse grid's own surface (bilinear reduces to the edge's straight line).
          const gi = I / f;
          const gj = J / f;
          const ci = Math.min(i1 - i0 - 1, Math.floor(gi));
          const cj = Math.min(j1 - j0 - 1, Math.floor(gj));
          const u = gi - ci;
          const w = gj - cj;
          const a00 = coarse(i0 + ci, j0 + cj);
          const a10 = coarse(i0 + ci + 1, j0 + cj);
          const a01 = coarse(i0 + ci, j0 + cj + 1);
          const a11 = coarse(i0 + ci + 1, j0 + cj + 1);
          v = a00 * (1 - u) * (1 - w) + a10 * u * (1 - w) + a01 * (1 - u) * w + a11 * u * w;
        } else v = this.ground(x0 + i0 * cell + I * fc, y0 + j0 * cell + J * fc, true);
        h[J * (W + 1) + I] = v;
      }
    }
    const at = (I: number, J: number): number => h[Math.max(0, Math.min(H, J)) * (W + 1) + Math.max(0, Math.min(W, I))];
    const normal = (I: number, J: number): [number, number, number] => {
      const dx = (at(I + 1, J) - at(I - 1, J)) / (2 * fc);
      const dy = (at(I, J + 1) - at(I, J - 1)) / (2 * fc);
      const l = Math.hypot(dx, 1, dy);
      return [-dx / l, 1 / l, dy / l];
    };
    const c = look().ground.grass;
    for (let J = 0; J < H; J++) {
      for (let I = 0; I < W; I++) {
        const quad = [
          [I, J],
          [I + 1, J],
          [I + 1, J + 1],
          [I, J + 1],
        ];
        for (const tri of [
          [0, 1, 2],
          [0, 2, 3],
        ]) {
          for (const k of tri) {
            const [a, b] = quad[k];
            pos.push(x0 + i0 * cell + a * fc, at(a, b), -(y0 + j0 * cell + b * fc));
            col.push(c.r, c.g, c.b);
            nor.push(...normal(a, b));
          }
        }
      }
    }
  }
}

interface Box {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * Where the road's height bends too sharply for the coarse terrain grid: across a cell (up to ~28 m, a
 * phone's diagonal) a straight line would stray more than 0.3 m from the road, more than the ground is
 * tucked under it. Each stretch, with 30 m either side and the ground beside the road, as a box.
 */
export function roughStretches(track: TrackData): Box[] {
  const n = track.n;
  const limit = (0.3 * 8) / (28 * 28);
  const rough: boolean[] = [];
  for (let i = 0; i < n; i++) {
    const a = (i - 1 + n) % n;
    const b = (i + 1) % n;
    const ds = ((track.s[b] - track.s[a] + track.length) % track.length) / 2 || 1;
    rough.push(Math.abs((track.elev[b] - 2 * track.elev[i] + track.elev[a]) / (ds * ds)) > limit);
  }
  if (!rough.some(Boolean)) return [];
  // Widen each rough sample by 30 m of road either way, so a stretch's ends are smooth.
  const step = track.length / n;
  const reach = Math.ceil(30 / step);
  const near = new Array<boolean>(n).fill(false);
  for (let i = 0; i < n; i++) if (rough[i]) for (let k = -reach; k <= reach; k++) near[(i + k + n) % n] = true;
  // Stretches of consecutive marked samples (one may wrap past the start line).
  const boxes: Box[] = [];
  const pad = track.halfWidth + 34;
  const start = near.indexOf(false);
  if (start < 0) {
    boxes.push(boxOf(track, 0, n - 1, pad));
    return boxes;
  }
  let from = -1;
  for (let k = 1; k <= n; k++) {
    const i = (start + k) % n;
    if (near[i] && from < 0) from = i;
    if (!near[i] && from >= 0) {
      boxes.push(boxOf(track, from, (i - 1 + n) % n, pad));
      from = -1;
    }
  }
  return boxes;
}

function boxOf(track: TrackData, from: number, to: number, pad: number): Box {
  const box = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
  for (let i = from; ; i = (i + 1) % track.n) {
    box.minX = Math.min(box.minX, track.px[i] - pad);
    box.maxX = Math.max(box.maxX, track.px[i] + pad);
    box.minY = Math.min(box.minY, track.py[i] - pad);
    box.maxY = Math.max(box.maxY, track.py[i] + pad);
    if (i === to) break;
  }
  return box;
}

/** Boxes that overlap (or come within `gap` of each other) merged into one, until none do. */
export function mergeBoxes(boxes: Box[], gap: number): Box[] {
  const out = boxes.map((b) => ({ ...b }));
  for (let merged = true; merged; ) {
    merged = false;
    for (let a = 0; a < out.length && !merged; a++) {
      for (let b = a + 1; b < out.length && !merged; b++) {
        const A = out[a];
        const B = out[b];
        if (A.minX - gap <= B.maxX && B.minX - gap <= A.maxX && A.minY - gap <= B.maxY && B.minY - gap <= A.maxY) {
          out[a] = { minX: Math.min(A.minX, B.minX), maxX: Math.max(A.maxX, B.maxX), minY: Math.min(A.minY, B.minY), maxY: Math.max(A.maxY, B.maxY) };
          out.splice(b, 1);
          merged = true;
        }
      }
    }
  }
  return out;
}

function smooth(t: number): number {
  const x = Math.max(0, Math.min(1, t));
  return x * x * (3 - 2 * x);
}
